import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useOrthodontistDetail } from '../../hooks/useOrthodontistDetail';
import { usePageTitle } from '../../hooks/usePageTitle';
import { OrthodontistDetailHeader } from '../../components/accounts/orthodontist/OrthodontistDetailHeader';
import {
    DEFAULT_TAB,
    isDetailTab,
    type DetailTabId
} from '../../components/accounts/orthodontist/detailTabs';
import { OrthodontistDetailsTab } from '../../components/accounts/orthodontist/details/OrthodontistDetailsTab';
import { OrthodontistUsersTab } from '../../components/accounts/orthodontist/users/OrthodontistUsersTab';
import { OrthodontistPatientsTab } from '../../components/accounts/orthodontist/patients/OrthodontistPatientsTab';

// Same `?tab=` + `replace` convention as PracticeDetail's readTabFromSearch -
// a missing/garbage param falls back to the default rather than a blank pane.
function readTabFromSearch(search: string): DetailTabId {
    const raw = new URLSearchParams(search).get('tab');
    return raw && isDetailTab(raw) ? raw : DEFAULT_TAB;
}

export function OrthodontistDetail() {
    const { orthodontistId } = useParams<{ orthodontistId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<DetailTabId>(() =>
        readTabFromSearch(location.search)
    );

    useEffect(() => {
        const next = readTabFromSearch(location.search);
        setActiveTab((prev) => (next === prev ? prev : next));
    }, [location.search]);

    const handleTabChange = (tab: DetailTabId) => {
        setActiveTab(tab);
        const params = new URLSearchParams(location.search);
        if (tab === DEFAULT_TAB) {
            params.delete('tab');
        } else {
            params.set('tab', tab);
        }
        const search = params.toString();
        navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
    };

    const { orthodontist, practices, isLoading, isError, refetch } =
        useOrthodontistDetail(orthodontistId);
    const practiceIds = useMemo(() => practices.map((practice) => practice.id), [practices]);

    usePageTitle(orthodontist?.name ?? null);

    if (!orthodontistId) {
        return <Navigate to="/accounts/orthodontists" replace />;
    }

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
            </div>
        );
    }

    if (isError || !orthodontist) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <div className="text-sm text-red-500">Failed to load this orthodontist</div>
                <button
                    onClick={() => refetch()}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <OrthodontistDetailHeader
                orthodontist={orthodontist}
                practiceCount={practices.length}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />
            {activeTab === 'details' && (
                <div className="flex-1 min-h-0 flex flex-col px-8 pb-4">
                    <OrthodontistDetailsTab orthodontist={orthodontist} practices={practices} />
                </div>
            )}
            {activeTab === 'users' && (
                <div className="flex-1 min-h-0 flex flex-col px-8 pb-4">
                    <OrthodontistUsersTab
                        orthodontistId={orthodontist.id}
                        orthodontistName={orthodontist.name}
                    />
                </div>
            )}
            {activeTab === 'patients' && (
                <div className="flex-1 min-h-0 flex flex-col px-8 pb-4">
                    <OrthodontistPatientsTab practiceIds={practiceIds} />
                </div>
            )}
        </div>
    );
}
